import { type Group, type GroupMember, useGroup, useGroupMembers } from '@hobbytools/data';
import { AppShell, useAuth } from '@hobbytools/platform';
import { Banner } from '@hobbytools/ui';
import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router';
import { linkClass } from './AuthLayout';

export interface GroupContext {
  group: Group;
  members: GroupMember[];
  me: GroupMember | undefined;
  userId: string;
  /** Approved admin or owner. */
  canManage: boolean;
}

export const useGroupContext = () => useOutletContext<GroupContext>();

const tabClass =
  'rounded-md px-3 py-2 text-sm font-medium text-fg-muted hover:bg-sunken aria-[current=page]:bg-accent-soft aria-[current=page]:text-accent-fg';

/** Shared header and routed tabs for a group. Every tab has its own URL. */
export function GroupLayout() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const group = useGroup(id);
  const members = useGroupMembers(id);
  const me = members.data?.find((m) => m.userId === user?.id);
  const canManage = me?.status === 'approved' && (me.role === 'owner' || me.role === 'admin');

  return (
    <AppShell current="hub">
      {group.isLoading || members.isLoading ? (
        <p className="text-fg-muted">Loading group…</p>
      ) : !group.data || !user ? (
        <div className="flex flex-col gap-3">
          <h1 className="heading-display text-2xl">Group not found</h1>
          <p className="text-fg-muted">It may have been removed, or you may not be a member.</p>
          <Link className={linkClass} to="/">
            Back to your groups
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <header className="flex flex-col gap-1">
            <p className="eyebrow">Group</p>
            <h1 className="heading-display text-3xl">{group.data.name}</h1>
            {group.data.description ? (
              <p className="max-w-[65ch] text-fg-muted">{group.data.description}</p>
            ) : null}
          </header>

          {me?.status === 'pending' ? (
            <Banner>Your request to join is waiting for an admin to approve it.</Banner>
          ) : null}

          {canManage ? (
            <nav aria-label="Group sections" className="flex gap-1 border-b border-border pb-2">
              <NavLink end to={`/groups/${id}`} className={tabClass}>
                Members
              </NavLink>
              <NavLink to={`/groups/${id}/settings`} className={tabClass}>
                Settings
              </NavLink>
            </nav>
          ) : null}

          <Outlet
            context={
              {
                group: group.data,
                members: members.data ?? [],
                me,
                userId: user.id,
                canManage,
              } satisfies GroupContext
            }
          />
        </div>
      )}
    </AppShell>
  );
}
